import * as React from "react"
import { X } from "lucide-react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import type { CreateGapDeviceRequest } from "@/types/gap-device"
import type { GapClientConfig } from "@/types/gap-client"
import { useNumericInput, type NumericInputValue } from "@/hooks/useNumericInput"

interface AddDeviceFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateGapDeviceRequest) => Promise<void>
  isSubmitting?: boolean
  clients?: GapClientConfig[]
}

export interface NewDevice {
  id: string
}

interface FormData {
  deviceId: string
  vendorDeviceId: string
  vendorName: string
  clientId: string
  areaCode: string
  necThreshold: NumericInputValue
  necThresholdUseDefault: boolean
  userInstructionsEnabled: boolean
  userInstructionsUseDefault: boolean
}

interface FormErrors {
  deviceId?: string
  vendorDeviceId?: string
  vendorName?: string
  clientId?: string
  areaCode?: string
  userInstructionsEnabled?: string
}

const initialFormData: FormData = {
  deviceId: "",
  vendorDeviceId: "",
  vendorName: "",
  clientId: "",
  areaCode: "",
  necThreshold: "",
  necThresholdUseDefault: true,
  userInstructionsEnabled: false,
  userInstructionsUseDefault: true,
}

export function AddDeviceForm({ open, onOpenChange, onSubmit, isSubmitting = false, clients = [] }: AddDeviceFormProps) {
  const [formData, setFormData] = React.useState<FormData>(initialFormData)
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const firstInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (open) {
      setFormData(initialFormData)
      setErrors({})
      setTouched({})
      setTimeout(() => firstInputRef.current?.focus(), 100)
    }
  }, [open])

  const necThresholdInput = useNumericInput({
    min: 0,
    max: 10000,
    integer: true,
    value: formData.necThreshold,
    validate: (value) => {
      if (value === "") return "NEC Match Threshold must be a valid number between 0 and 10000"
      if (!Number.isInteger(value) || value < 0 || value > 10000) {
        return "NEC Match Threshold must be a valid number between 0 and 10000"
      }
      return undefined
    },
  })

  const validateField = (name: Exclude<keyof FormData, "necThreshold">, value: string): string | undefined => {
    switch (name) {
      case "deviceId":
        if (!value.trim()) return "Device ID is required"
        if (!/^[A-Za-z0-9-]+$/.test(value)) return "Device ID can only contain letters, numbers, and hyphens"
        return undefined
      case "vendorDeviceId":
        if (!value.trim()) return "Vendor Device ID is required"
        return undefined
      case "vendorName":
        if (!value) return "Vendor Name is required"
        return undefined
      case "clientId":
        if (!value) return "Client ID is required"
        return undefined
      case "areaCode":
        if (!value.trim()) return "Area Code is required"
        return undefined
      default:
        return undefined
    }
  }

  const handleBlur = (name: keyof FormData) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    if (name === "necThreshold") return
    const error = validateField(name, formData[name] as string)
    if (name in { deviceId: true, vendorDeviceId: true, vendorName: true, clientId: true, areaCode: true, userInstructionsEnabled: true }) {
      setErrors((prev) => {
        const next = { ...prev }
        if (error) {
          next[name as keyof FormErrors] = error
        } else {
          delete next[name as keyof FormErrors]
        }
        return next
      })
    }
  }

  const handleChange = (name: keyof FormData, value: string | boolean | NumericInputValue) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (name === "necThreshold") return
    if (touched[name] && typeof value === "string") {
      const error = validateField(name, value)
      if (name in { deviceId: true, vendorDeviceId: true, vendorName: true, clientId: true, areaCode: true, userInstructionsEnabled: true }) {
        setErrors((prev) => {
          const next = { ...prev }
          if (error) {
            next[name as keyof FormErrors] = error
          } else {
            delete next[name as keyof FormErrors]
          }
          return next
        })
      }
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    const fields: Array<keyof FormErrors> = ["deviceId", "vendorDeviceId", "vendorName", "clientId", "areaCode"]

    fields.forEach((field) => {
      const error = validateField(field, formData[field] as string)
      if (error) newErrors[field] = error
    })

    setErrors(newErrors)
    setTouched({ deviceId: true, vendorDeviceId: true, vendorName: true, clientId: true, areaCode: true })
    return Object.keys(newErrors).length === 0
  }

  const necThresholdInvalid =
    !formData.necThresholdUseDefault &&
    (necThresholdInput.error || formData.necThreshold === "")

  const canSubmit = !necThresholdInvalid && Object.keys(errors).length === 0

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (necThresholdInvalid) {
      setTouched((prev) => ({ ...prev, necThreshold: true }))
      return
    }

    const createData: CreateGapDeviceRequest = {
      deviceId: formData.deviceId,
      clientId: formData.clientId,
      areaCode: formData.areaCode,
      vendorName: formData.vendorName,
      vendorDeviceId: formData.vendorDeviceId,
      necMatchScoreThreshold: formData.necThresholdUseDefault
        ? { useDefault: true } // Omit value when using default
        : { useDefault: false, value: Math.round(formData.necThreshold as number) },
      featureFlagUserInstructions: formData.userInstructionsUseDefault
        ? { useDefault: true } // Omit value when using default
        : { useDefault: false, value: formData.userInstructionsEnabled },
    }

    await onSubmit(createData)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[540px] sm:max-w-[540px] bg-card border-l border-border p-0 flex flex-col [&>button]:hidden"
      >
        {/* Header */}
        <SheetHeader className="px-6 py-5 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-lg font-semibold text-foreground">Add New Device</SheetTitle>
              <SheetDescription className="text-muted-foreground mt-1">
                Configure a new device for the system
              </SheetDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground hover:bg-secondary -mr-2 -mt-1"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Device Identification Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Device Identification
              </h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Device ID */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Device ID <span className="text-destructive">*</span>
              </Label>
              <Input
                ref={firstInputRef}
                value={formData.deviceId}
                onChange={(e) => handleChange("deviceId", e.target.value)}
                onBlur={() => handleBlur("deviceId")}
                placeholder="e.g., DEV-001-A7F3"
                className={`font-mono bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.deviceId && touched.deviceId ? "border-destructive" : ""
                }`}
              />
              {errors.deviceId && touched.deviceId && <p className="text-xs text-destructive">{errors.deviceId}</p>}
            </div>

            {/* Vendor Device ID */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Vendor Device ID <span className="text-destructive">*</span>
              </Label>
              <Input
                value={formData.vendorDeviceId}
                onChange={(e) => handleChange("vendorDeviceId", e.target.value)}
                onBlur={() => handleBlur("vendorDeviceId")}
                placeholder="Vendor's device identifier"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.vendorDeviceId && touched.vendorDeviceId ? "border-destructive" : ""
                }`}
              />
              {errors.vendorDeviceId && touched.vendorDeviceId && (
                <p className="text-xs text-destructive">{errors.vendorDeviceId}</p>
              )}
            </div>

            {/* Vendor Name */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Vendor Name <span className="text-destructive">*</span>
              </Label>
              <Input
                value={formData.vendorName}
                onChange={(e) => handleChange("vendorName", e.target.value)}
                onBlur={() => handleBlur("vendorName")}
                placeholder="e.g., PayFace Pro"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.vendorName && touched.vendorName ? "border-destructive" : ""
                }`}
              />
              {errors.vendorName && touched.vendorName && (
                <p className="text-xs text-destructive">{errors.vendorName}</p>
              )}
            </div>

            {/* Area Code */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Area Code <span className="text-destructive">*</span>
              </Label>
              <Input
                value={formData.areaCode}
                onChange={(e) => handleChange("areaCode", e.target.value)}
                onBlur={() => handleBlur("areaCode")}
                placeholder="e.g., 212"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                  errors.areaCode && touched.areaCode ? "border-destructive" : ""
                }`}
              />
              {errors.areaCode && touched.areaCode && <p className="text-xs text-destructive">{errors.areaCode}</p>}
            </div>
          </div>

          {/* Client Association Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Client Association</h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Client ID */}
            <div className="space-y-2">
              <Label className="text-[13px] text-muted-foreground font-medium">
                Client ID <span className="text-destructive">*</span>
              </Label>
              <Select value={formData.clientId} onValueChange={(value) => handleChange("clientId", value)}>
                <SelectTrigger
                  onBlur={() => handleBlur("clientId")}
                  className={`w-full bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] ${
                    errors.clientId && touched.clientId ? "border-destructive" : ""
                  }`}
                >
                  <SelectValue placeholder={clients.length === 0 ? "Loading clients..." : "Select a client"} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {clients.length === 0 ? (
                    <div className="py-6 px-2 text-center text-sm text-muted-foreground">
                      No clients available
                    </div>
                  ) : (
                    clients.map((client) => (
                      <SelectItem key={client.clientId} value={client.clientId} className="text-foreground focus:bg-secondary">
                        {client.clientId}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.clientId && touched.clientId && <p className="text-xs text-destructive">{errors.clientId}</p>}
              {clients.length === 0 && (
                <p className="text-xs text-muted-foreground">No clients found. Please create a client first.</p>
              )}
            </div>
          </div>

          {/* Configuration Settings Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Configuration Settings
              </h3>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* NEC Match Score Threshold */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[13px] text-muted-foreground font-medium">NEC Match Score Threshold</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="necThresholdUseDefault"
                    checked={formData.necThresholdUseDefault}
                    onChange={(e) => {
                      handleChange("necThresholdUseDefault", e.target.checked)
                    }}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <Label htmlFor="necThresholdUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                    Use default
                  </Label>
                </div>
              </div>
              <Input
                type="number"
                min={0}
                max={10000}
                step={1}
                value={necThresholdInput.inputValue}
                onChange={(e) => {
                  necThresholdInput.handleChange(e)
                  const nextRaw = e.target.value
                  let nextValue: NumericInputValue = ""
                  if (nextRaw !== "") {
                    const parsed = Number(nextRaw)
                    nextValue = Number.isNaN(parsed) ? "" : parsed
                  }
                  setFormData((prev) => ({ ...prev, necThreshold: nextValue }))
                }}
                onBlur={() => {
                  necThresholdInput.handleBlur()
                  setTouched((prev) => ({ ...prev, necThreshold: true }))
                }}
                placeholder="e.g., 8500"
                disabled={formData.necThresholdUseDefault}
                autoComplete="off"
                className={`bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] disabled:opacity-50 disabled:cursor-not-allowed ${
                  !formData.necThresholdUseDefault && touched.necThreshold && necThresholdInput.error ? "border-destructive" : ""
                }`}
              />
              {!formData.necThresholdUseDefault && touched.necThreshold && necThresholdInput.error ? (
                <p className="text-xs text-destructive">{necThresholdInput.error}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {formData.necThresholdUseDefault
                    ? "Will use the client's default threshold"
                    : "Enter an integer between 0 and 10000"}
                </p>
              )}
            </div>

            {/* User Instructions Toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-[13px] text-muted-foreground font-medium">User Instructions</Label>
                  <p className="text-xs text-muted-foreground">Enable to show user instructions on device</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="userInstructionsUseDefault"
                    checked={formData.userInstructionsUseDefault}
                    onChange={(e) => {
                      handleChange("userInstructionsUseDefault", e.target.checked)
                    }}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                  />
                  <Label htmlFor="userInstructionsUseDefault" className="text-xs text-muted-foreground cursor-pointer">
                    Use default
                  </Label>
                </div>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-muted-foreground">
                  {formData.userInstructionsUseDefault ? "Will use client setting" : "Device-specific setting"}
                </span>
                <Switch
                  checked={formData.userInstructionsEnabled}
                  onCheckedChange={(checked) => handleChange("userInstructionsEnabled", checked)}
                  disabled={formData.userInstructionsUseDefault}
                  className="data-[state=checked]:bg-primary disabled:opacity-50"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSubmitting && <LoadingSpinner size="sm" className="mr-2" />}
            {isSubmitting ? "Creating..." : "Create Device"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
